import { atom, DefaultValue, selector } from "recoil"
import * as QueryString from "query-string"
import { TaskSidebar, TimelineSidebar, WorksetSidebar, Notification } from "../types"
import { getLoggedInUserProjectRole, getProjectUsers, getUserGroups } from "./stateQueries"
import { TCExtensionAPI } from "trimble-connect-project-workspace-api"
import { SnackProps, SnackWithIdProps } from "./Config/SnackBars"
import { ConnectUser } from "../api/ConnectApi"

export const sidebarTaskState = atom({
  key: "sidebarTaskState",
  default: null as TaskSidebar | null
})

export const sidebarTimelineState = atom({
  key: "sidebarTimelineState",
  default: null as TimelineSidebar | null
})

export const sidebarWorksetState = atom({
  key: "sidebarWorksetState",
  default: null as WorksetSidebar | null
})

export const selectedTaskRow = atom({
  key: "selectedTask",
  default: [] as number[]
})

export const projectIdState = atom<string | undefined>({
  key: "projectIdState",
  default: undefined
})

export const quadriProjectIdState = atom<string | undefined>({
  key: "quadriProjectIdState",
  default: undefined
})

export const projectRegionState = atom<string | undefined>({
  key: "projectRegionState",
  default: undefined
})

export const featureCatalogIdState = atom<string | undefined>({
  key: "featureCatalogIdState",
  default: undefined
})

export const needsLicenseState = atom<boolean | undefined>({
  key: "needsLicense",
  default: undefined
})

export const hasProjectAccessState = atom<boolean | undefined>({
  key: "hasProjectAccess",
  default: undefined
})

export const notificationsState = atom<Notification[] | undefined>({
  key: "notifications",
  default: undefined
})

export const signedInUserState = atom<string | undefined>({
  key: "signedInUser",
  default: undefined
})

const privateDefaultProjectOriginState = selector({
  key: "privateDefaultProjectOriginState",
  get: () => {
    // eslint-disable-next-line no-restricted-globals
    const params = QueryString.parse(location.search)
    if (params.origin) {
      return `https:${params.origin.toString()}/tc/api/2.0`
    }
    return "https://app21.connect.trimble.com/tc/api/2.0"
  }
})

const privateProjectOriginState = atom({
  key: "privateProjectOriginState",
  default: privateDefaultProjectOriginState
})

export const projectOriginState = selector({
  key: "projectOriginState",
  get: ({ get }) => get(privateProjectOriginState),
  set: ({ set }, newProjectOrigin: string | DefaultValue) =>
    set(
      privateProjectOriginState,
      newProjectOrigin instanceof DefaultValue ? newProjectOrigin : `https:${newProjectOrigin}/tc/api/2.0`
    )
})

export const userClaimsState = atom({
  key: "UserClaimsState",
  default: undefined as undefined | any
})

/** Selector for getting the users list, based on connect project ID and origin */
export const usersByTidUuidState = selector({
  key: "UsersByTidUuidState",
  get: async ({ get }) => {
    const users = await getProjectUsers({
      connectProjectId: get(projectIdState),
      connectProjectOrigin: get(projectOriginState)
    })

    let usersByTidUuid: { [key: string]: ConnectUser } = {}

    for (const u of users) {
      if (u.tiduuid) {
        usersByTidUuid[u.tiduuid] = u
      }
    }

    return usersByTidUuid
  }
})

/** Selector for getting the users list, based on connect project ID and origin */
export const projectUsersState = selector({
  key: "ProjectUsersState",
  get: async ({ get }) => {
    const projectUsers = await getProjectUsers({
      connectProjectId: get(projectIdState),
      connectProjectOrigin: get(projectOriginState)
    })
    return projectUsers
  }
})

/** Selector for getting the user groups list, based on connect project ID and origin */
export const userGroupsState = selector({
  key: "UserGroupsState",
  get: async ({ get }) => {
    const userGroups = await getUserGroups({
      connectProjectId: get(projectIdState),
      connectProjectOrigin: get(projectOriginState)
    })
    return userGroups
  }
})

export const extensionApiState = atom({
  key: "ExtensionApiState",
  default: undefined as TCExtensionAPI | undefined
})

export const userProjectRoleState = selector({
  key: "UserProjectRoleState",
  get: async ({ get }) => {
    const userProjectRole = await getLoggedInUserProjectRole({
      connectProjectId: get(projectIdState),
      connectProjectOrigin: get(projectOriginState)
    })
    return userProjectRole
  }
})

export const snacksState = atom({
  key: "SnacksState",
  default: [] as SnackWithIdProps[]
})

export const snackIdState = atom({
  key: "SnackIdState",
  default: 0
})

export const addSnackSelector = selector({
  key: "AddSnack",
  get: ({ get }) => null as SnackProps | null, // This is a set only selector
  set: ({ get, set }, snackProps: SnackProps | null | DefaultValue) => {
    const snackId = get(snackIdState)
    const snacks = get(snacksState)
    const id = `snack-${snackId}`
    const toAdd: SnackWithIdProps = { ...(snackProps as SnackProps), id }
    set(snackIdState, snackId + 1)
    set(snacksState, [...snacks, toAdd])
  }
})

export const removeSnackSelector = selector({
  key: "RemoveSnack",
  get: ({ get }) => null as string | null, // This is a set only selector
  set: ({ get, set }, id: string | null | DefaultValue) => {
    const snacks = get(snacksState)
    set(
      snacksState,
      snacks.filter((s) => s.id !== id)
    )
  }
})
